/**
 * DOM Utilities
 * 
 * Utilidades para interactuar con el DOM de manera segura y robusta,
 * especialmente útil cuando se trabaja con librerías de terceros que
 * manipulan el DOM de manera asíncrona (como Syncfusion).
 */

/**
 * Espera a que un elemento específico esté disponible en el DOM
 * 
 * Usa MutationObserver para detectar cambios en tiempo real + polling 
 * con exponential backoff como fallback. Esta función es especialmente 
 * útil para evitar race conditions cuando se trabaja con componentes React
 * que dependen de elementos renderizados por librerías de terceros.
 * 
 * @param {string} elementId - ID del elemento a observar
 * @param {Function} callback - Función a ejecutar cuando el elemento esté disponible
 * @param {Object} options - Opciones de configuración
 * @param {number} [options.maxAttempts=30] - Número máximo de intentos de polling
 * @param {number} [options.initialDelay=100] - Delay inicial en ms para el primer intento
 * @param {number} [options.maxDelay=1000] - Delay máximo en ms entre intentos (exponential backoff)
 * @param {number} [options.timeout=5000] - Timeout global en ms
 * @param {boolean} [options.useObserver=true] - Si usar MutationObserver (recomendado)
 * @param {string} [options.containerSelector='#schedule'] - Selector del contenedor a observar
 * 
 * @example
 * // Uso básico
 * waitForElement('myElement', (element) => {
 *   console.log('Element found!', element);
 * });
 * 
 * @example
 * // Con opciones personalizadas
 * waitForElement('toolbarButton', (element) => {
 *   element.addEventListener('click', handleClick);
 * }, {
 *   maxAttempts: 20,
 *   initialDelay: 50,
 *   timeout: 3000
 * });
 * 
 * @returns {void}
 */
export function waitForElement(elementId, callback, options = {}) {
    const {
        maxAttempts = 30,
        initialDelay = 100,
        maxDelay = 1000,
        timeout = 5000,
        useObserver = true,
        containerSelector = '#schedule'
    } = options;

    let attempts = 0;
    let currentDelay = initialDelay;
    let timeoutId = null;
    let observer = null;

    // Función de limpieza para prevenir memory leaks
    const cleanup = () => {
        if (timeoutId) clearTimeout(timeoutId);
        if (observer) observer.disconnect();
    };

    // Verificar si el elemento ya existe en el DOM
    const checkElement = () => {
        const element = document.getElementById(elementId);
        if (element) {
            cleanup();
            callback(element);
            return true;
        }
        return false;
    };

    // Si el elemento ya existe, ejecutar callback inmediatamente
    if (checkElement()) return;

    // Usar MutationObserver si está disponible y habilitado
    if (useObserver && typeof MutationObserver !== 'undefined') {
        observer = new MutationObserver(() => {
            if (checkElement()) {
                console.log(`✅ Element '${elementId}' detected via MutationObserver`);
            }
        });

        // Observar el contenedor especificado
        const container = document.querySelector(containerSelector);
        if (container) {
            observer.observe(container, {
                childList: true,   // Detecta agregación/eliminación de nodos
                subtree: true,     // Observa todo el subárbol
                attributes: false  // No observar cambios de atributos (mejor performance)
            });
        } else {
            console.warn(`⚠️ Container '${containerSelector}' not found for MutationObserver`);
        }
    }

    // Polling con exponential backoff como fallback
    const poll = () => {
        if (checkElement()) {
            console.log(`✅ Element '${elementId}' found via polling (attempt ${attempts + 1})`);
            return;
        }

        attempts++;
        if (attempts >= maxAttempts) {
            cleanup();
            console.warn(`⚠️ Element '${elementId}' not found after ${maxAttempts} attempts`);
            return;
        }

        // Exponential backoff: incrementar el delay hasta maxDelay
        currentDelay = Math.min(currentDelay * 1.5, maxDelay);
        timeoutId = setTimeout(poll, currentDelay);
    };

    // Iniciar polling después de un delay inicial
    timeoutId = setTimeout(poll, initialDelay);

    // Timeout de seguridad global
    setTimeout(() => {
        cleanup();
        if (!document.getElementById(elementId)) {
            console.warn(`⚠️ Element '${elementId}' not found after ${timeout}ms timeout`);
        }
    }, timeout);
}

/**
 * Espera a que múltiples elementos estén disponibles en el DOM
 * 
 * @param {string[]} elementIds - Array de IDs de elementos a observar
 * @param {Function} callback - Función a ejecutar cuando TODOS los elementos estén disponibles
 * @param {Object} options - Opciones de configuración (mismas que waitForElement)
 * 
 * @example
 * waitForElements(['button1', 'button2', 'button3'], (elements) => {
 *   console.log('All elements found!', elements);
 * });
 * 
 * @returns {void}
 */
export function waitForElements(elementIds, callback, options = {}) {
    const foundElements = {};
    let remainingCount = elementIds.length;

    elementIds.forEach(elementId => {
        waitForElement(elementId, (element) => {
            foundElements[elementId] = element;
            remainingCount--;

            if (remainingCount === 0) {
                callback(foundElements);
            }
        }, options);
    });
}

/**
 * Verifica si un elemento existe en el DOM
 * 
 * @param {string} elementId - ID del elemento a verificar
 * @returns {boolean} - true si el elemento existe, false en caso contrario
 * 
 * @example
 * if (elementExists('myElement')) {
 *   console.log('Element exists!');
 * }
 */
export function elementExists(elementId) {
    return document.getElementById(elementId) !== null;
}

/**
 * Espera a que un selector CSS esté disponible en el DOM
 * Similar a waitForElement pero usa querySelector en lugar de getElementById
 * 
 * @param {string} selector - Selector CSS del elemento a observar
 * @param {Function} callback - Función a ejecutar cuando el elemento esté disponible
 * @param {Object} options - Opciones de configuración
 * 
 * @example
 * waitForSelector('.e-schedule-toolbar .e-toolbar-item', (element) => {
 *   console.log('Toolbar item found!', element);
 * });
 * 
 * @returns {void}
 */
export function waitForSelector(selector, callback, options = {}) {
    const {
        maxAttempts = 30,
        initialDelay = 100,
        maxDelay = 1000,
        timeout = 5000,
        useObserver = true,
        containerSelector = 'body'
    } = options;

    let attempts = 0;
    let currentDelay = initialDelay;
    let timeoutId = null;
    let observer = null;

    const cleanup = () => {
        if (timeoutId) clearTimeout(timeoutId);
        if (observer) observer.disconnect();
    };

    const checkElement = () => {
        const element = document.querySelector(selector);
        if (element) {
            cleanup();
            callback(element);
            return true;
        }
        return false;
    };

    if (checkElement()) return;

    if (useObserver && typeof MutationObserver !== 'undefined') {
        observer = new MutationObserver(() => {
            if (checkElement()) {
                console.log(`✅ Element '${selector}' detected via MutationObserver`);
            }
        });

        const container = document.querySelector(containerSelector);
        if (container) {
            observer.observe(container, {
                childList: true,
                subtree: true,
                attributes: false
            });
        }
    }

    const poll = () => {
        if (checkElement()) {
            console.log(`✅ Element '${selector}' found via polling (attempt ${attempts + 1})`);
            return;
        }

        attempts++;
        if (attempts >= maxAttempts) {
            cleanup();
            console.warn(`⚠️ Element '${selector}' not found after ${maxAttempts} attempts`);
            return;
        }

        currentDelay = Math.min(currentDelay * 1.5, maxDelay);
        timeoutId = setTimeout(poll, currentDelay);
    };

    timeoutId = setTimeout(poll, initialDelay);

    setTimeout(() => {
        cleanup();
        if (!document.querySelector(selector)) {
            console.warn(`⚠️ Element '${selector}' not found after ${timeout}ms timeout`);
        }
    }, timeout);
}

